/**
 * The local folder caught up with the cloud: media that is only in the cloud (generated or
 * uploaded while no folder was connected, or on another device) is written into the folder,
 * each file downloaded once, under the workspace's place and with the cloud copy's own category
 * and name, where every later look finds it (utils/localSave.ts). The app runs this in the
 * background when a folder is connected and when a workspace opens with one, and the folder
 * window runs it for every file of the library. Nothing here ever asks for access to the folder.
 */
import type { CanvasNode, HistoryItem, LightHistoryItem } from '../types';
import { createFileReference, extractFileId, getFileMeta, isFileReference, knownStoragePath, peekFileMeta, type FileMetadata } from './fileRegistry';
import { autoSaveFile, getLocalFolderAccess, locateLocalFile, parseStoragePath, registerLocalFileMapping, storageWorkspaceOf, type SaveCategory } from './localSave';
import { loadMediaWithSource, storagePathFromDownloadUrl } from './mediaLoader';

/** A file to have in the folder: its reference as a node holds it, and the workspace whose place it goes under */
export interface CatchUpEntry {
    ref: string;
    workspaceName: string;
    /** The owner of a shared workspace, whose registry has the file */
    ownerUserId?: string;
}

/** Where the work is: how many files are dealt with, of how many, and the one at hand */
export interface CatchUpProgress {
    done: number;
    total: number;
    filename: string;
}

export interface CatchUpResult {
    /** Written into the folder now */
    copied: number;
    /** Already in the folder */
    present: number;
    /** Could not be downloaded or written */
    failed: number;
    /** Not known to the library (no cloud path), so left alone */
    unknown: number;
    /** Stopped before the end: no access to the folder, or told to stop */
    stopped: boolean;
}

export interface CatchUpOptions {
    entries: CatchUpEntry[];
    userId: string;
    onProgress?: (progress: CatchUpProgress) => void;
    signal?: AbortSignal;
}

/** Whether a string a node holds names a file in the cloud: a registry reference, or a Firebase download URL */
export const isCloudMediaRef = (value: unknown): value is string =>
    typeof value === 'string' && (isFileReference(value) || (/^https?:\/\//i.test(value) && storagePathFromDownloadUrl(value) !== null));

const walkStrings = (value: unknown, visit: (text: string) => void, depth = 0): void => {
    if (depth > 8 || value === null || value === undefined) return;
    if (typeof value === 'string') { visit(value); return; }
    if (Array.isArray(value)) { for (const item of value) walkStrings(item, visit, depth + 1); return; }
    if (typeof value === 'object') for (const item of Object.values(value as Record<string, unknown>)) walkStrings(item, visit, depth + 1);
};

/** Every cloud file a workspace's nodes and history refer to, once each, in the order first met */
export const cloudMediaRefs = (nodes: ReadonlyArray<CanvasNode>, history: ReadonlyArray<HistoryItem | LightHistoryItem> = []): string[] => {
    const refs = new Set<string>();
    const take = (text: string) => { if (isCloudMediaRef(text)) refs.add(text); };
    for (const node of nodes) walkStrings(node.data, take);
    for (const item of history) walkStrings(item, take);
    return Array.from(refs);
};

/** The entries of one workspace: its cloud files, under its own place */
export const workspaceCatchUpEntries = (
    nodes: ReadonlyArray<CanvasNode>,
    history: ReadonlyArray<HistoryItem | LightHistoryItem>,
    workspaceName: string,
    ownerUserId?: string | null,
): CatchUpEntry[] =>
    cloudMediaRefs(nodes, history).map(ref => ({ ref, workspaceName, ...(ownerUserId ? { ownerUserId } : {}) }));

/** Every file of the library as an entry, under the workspace its record names, else the one its cloud path names */
export const libraryCatchUpEntries = (files: ReadonlyArray<Pick<FileMetadata, 'id' | 'storagePath' | 'workspaceName'>>): CatchUpEntry[] =>
    files
        .map(file => ({ ref: createFileReference(file.id), workspaceName: (file.workspaceName || storageWorkspaceOf(file.storagePath) || '').trim() }))
        .filter(entry => entry.workspaceName.length > 0);

/** The cloud path of a file: a download URL says it; a registry reference is looked up, in what this device remembers first */
export const storagePathOfRef = async (ref: string, userId: string, ownerUserId?: string): Promise<string | null> => {
    if (!isFileReference(ref)) return storagePathFromDownloadUrl(ref);
    const fileId = extractFileId(ref);
    if (!fileId) return null;
    const remembered = knownStoragePath(userId, fileId)
        ?? peekFileMeta(userId, fileId)?.storagePath
        ?? (ownerUserId ? peekFileMeta(ownerUserId, fileId)?.storagePath : undefined);
    if (remembered) return remembered;
    try {
        const meta = (await getFileMeta(userId, fileId)) ?? (ownerUserId && ownerUserId !== userId ? await getFileMeta(ownerUserId, fileId) : null);
        return meta?.storagePath ?? null;
    } catch (e) {
        console.warn(`[folder] the library could not be asked about ${ref}:`, e);
        return null;
    }
};

const SAFE_NAME = /^[A-Za-z0-9_.-]+$/;
const extensionOf = (filename: string): string => /\.([A-Za-z0-9]+)$/.exec(filename)?.[1] ?? 'bin';

/**
 * Writes into the folder every entry's file that is not there: looked for first (its workspace's
 * place, where its folder was before, the workspace the cloud path names), then downloaded once
 * and written under the workspace's place with the cloud copy's category and name, and noted in
 * the workspace's map of local files. Files the library does not know are left alone; a failure
 * moves on to the next file; stopping (the signal) ends it after the file at hand. Without access
 * to the folder nothing is done, and nothing is asked.
 */
export const catchUpFolder = async ({ entries, userId, onProgress, signal }: CatchUpOptions): Promise<CatchUpResult> => {
    const result: CatchUpResult = { copied: 0, present: 0, failed: 0, unknown: 0, stopped: false };
    if (entries.length === 0) return result;
    const access = await getLocalFolderAccess(userId);
    if (access?.state !== 'granted') return { ...result, stopped: true };
    for (const [index, entry] of entries.entries()) {
        if (signal?.aborted) { result.stopped = true; break; }
        const storagePath = await storagePathOfRef(entry.ref, userId, entry.ownerUserId);
        const info = storagePath ? parseStoragePath(storagePath) : null;
        if (!storagePath || !info || !SAFE_NAME.test(info.category) || !SAFE_NAME.test(info.filename)) { result.unknown++; continue; }
        onProgress?.({ done: index, total: entries.length, filename: info.filename });
        try {
            if (await locateLocalFile(entry.workspaceName, info.category, info.filename, userId, storageWorkspaceOf(storagePath))) { result.present++; continue; }
            if (signal?.aborted) { result.stopped = true; break; }
            const loaded = await loadMediaWithSource(entry.ref, { userId, ownerUserId: entry.ownerUserId, workspaceName: entry.workspaceName, label: info.filename, keepInFolder: false });
            if (loaded.source === 'folder') { result.present++; continue; }
            const saved = await autoSaveFile(entry.workspaceName, info.category as SaveCategory, loaded.blob, extensionOf(info.filename), info.filename, userId);
            if (!saved.success) throw new Error(saved.error || 'not written');
            registerLocalFileMapping(entry.ref, `${info.category}/${info.filename}`);
            result.copied++;
        } catch (e) {
            result.failed++;
            console.warn(`[folder] ${info.filename} was not copied into the folder:`, e);
        }
    }
    return result;
};

/** "12 files copied to your folder", "1 file could not be copied": what to say when it is done; null when there is nothing to say */
export const catchUpSummary = (result: CatchUpResult, where: string = 'your folder'): string | null => {
    const files = (n: number) => `${n} file${n === 1 ? '' : 's'}`;
    if (result.copied === 0 && result.failed === 0) return null;
    const parts = [result.copied > 0 ? `${files(result.copied)} copied to ${where}` : null, result.failed > 0 ? `${files(result.failed)} could not be copied` : null].filter(Boolean);
    return `${parts.join(', ')}${result.stopped ? ' (stopped)' : ''}`;
};
